/**
 * Roles — sv_permissions' Casbin domain-RBAC, through the gateway's `/roles/*` family.
 *
 * ## The header is not optional, whatever the spec's `required: false` says
 *
 * Every write here resolves its Casbin domain through `company_or_system_domain`:
 * `x-company-id` present and a non-nil uuid means `company:{id}`; absent, empty or the
 * nil uuid means `system`. A company owner's seeded policy is `company:*, *, manage`
 * and they hold NOTHING in `system` — so a role assignment sent without the header is
 * not a wider call, it is a 403. These functions all go through `requestInCompany()`
 * for that reason, and take `companyId` first so it cannot be forgotten.
 *
 * (This is the shape of an earlier bug in this file's predecessor: `assignRole` and
 * `revokeRole` lived in `entities/member/api.ts` and used the plain `request()`, so
 * the role chips on the members screen were being authorized in `system` and answered
 * 403 for exactly the person they were built for.)
 *
 * ## What a "role" is not
 *
 * `POST /companies/{id}/members` also takes a `role`, and that one is a closed set of
 * three the ORG service understands (`entities/member/api.ts`). This file is the
 * catalogue: `GET /roles/definitions` is the list that actually exists, it is
 * localized, and it grows without this app being rebuilt. Prefer it for anything that
 * offers a choice.
 *
 * Two routes in the family are deliberately absent: `DELETE /roles/users/{id}` wipes
 * every domain a user holds anything in and is refused to anyone but a platform
 * administrator — its company-scoped stand-in is {@link revokeRolesInCompany}, which
 * the gateway's own error message names.
 */
import {request, requestInCompany} from '@/shared/api';
import type {BulkOutcome} from '@/shared/api/bulk';
import {runBulk} from '@/shared/api/bulk';
import {
  DOMAIN_TYPE_COMPANY,
  type RoleAssignment,
  type RoleAssignments,
  type RoleDefinition,
  type RoleDefinitions,
  type RoleHolders,
} from './types';

/**
 * The role catalogue.
 *
 * No Casbin check at all — reference data, open to any authenticated caller — so this
 * is a plain `request()`, and it is safe to fetch before a company is chosen.
 * `domainType` filters server-side; omit it to get every domain's roles.
 */
export async function fetchRoleDefinitions(
  domainType?: number,
): Promise<RoleDefinition[]> {
  const res = await request<RoleDefinitions>(
    domainType === undefined
      ? '/roles/definitions'
      : `/roles/definitions?domain_type=${domainType}`,
  );
  return res.roles ?? [];
}

/**
 * Every role assignment a user holds — in EVERY company, not just this one.
 *
 * The route has no caller-side scoping of its own (`get_user_roles` takes no headers
 * and applies no company comparison), so the answer routinely names companies the
 * caller has nothing to do with. Filter before showing: {@link rolesInCompany} is that
 * filter, and using it is not optional politeness — a member card that lists a
 * person's roles at their other employer is a leak, not a feature.
 */
export async function fetchUserRoles(
  companyId: string,
  userId: string,
): Promise<RoleAssignment[]> {
  const res = await requestInCompany<RoleAssignments>(
    `/roles/users/${encodeURIComponent(userId)}`,
    companyId,
  );
  return res.roles ?? [];
}

/** The subset of {@link fetchUserRoles} that belongs to one company, and is live. */
export function rolesInCompany(
  assignments: RoleAssignment[],
  companyId: string,
): RoleAssignment[] {
  const wanted = companyId.trim().toLowerCase();
  return assignments.filter(
    a => a.is_active !== false && (a.company_id ?? '').trim().toLowerCase() === wanted,
  );
}

/**
 * Everyone in this company holding one role.
 *
 * The odd one out of the family: `x-company-id` is REQUIRED here — `role_code` matches
 * none of the router's literal arms and falls into the branch's `require_company!`, so
 * a call without it is a 400 rather than the cross-tenant view its neighbours give.
 *
 * ⚠️ Paging is `page`/`page_size`, not the house `page`/`per_page`, and there is no
 * `search`.
 */
export function fetchRoleHolders(
  companyId: string,
  roleCode: string,
  params: {page?: number; pageSize?: number} = {},
): Promise<RoleHolders> {
  const qs = new URLSearchParams();
  if (params.page !== undefined) {
    qs.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    qs.set('page_size', String(params.pageSize));
  }
  const query = qs.toString();
  return requestInCompany<RoleHolders>(
    `/roles/${encodeURIComponent(roleCode)}/users${query ? `?${query}` : ''}`,
    companyId,
  );
}

function assignBody(companyId: string, userId: string, roleCode: string) {
  return JSON.stringify({
    user_id: userId,
    role_code: roleCode,
    // Required by the DTO, and ignored by the service: the role's own catalogue entry
    // decides the scope. Sending the company value keeps the request honest about what
    // the caller meant, nothing more.
    domain_type: DOMAIN_TYPE_COMPANY,
    // Checked against the domain the header authorized (`company_must_match_scope`).
    // It must equal the header, or the call is refused 403 company_scope_mismatch —
    // which is why both come from the same argument.
    company_id: companyId,
  });
}

/**
 * Grant a company-scoped role to an ACCOUNT.
 *
 * `userId` is `Member.account_id`, never the membership id — the two live in different
 * services and passing the wrong one fails at the far end, where the message is about
 * a user that does not exist rather than about the field that was wrong.
 *
 * `roleCode` must exist in {@link fetchRoleDefinitions} with `is_assignable: true`.
 */
export function assignRole(
  companyId: string,
  userId: string,
  roleCode: string,
): Promise<unknown> {
  return requestInCompany<unknown>('/roles/assign', companyId, {
    method: 'POST',
    body: assignBody(companyId, userId, roleCode),
  });
}

/** Take one role back. Revoking a role the user never held is not an error. */
export function revokeRole(
  companyId: string,
  userId: string,
  roleCode: string,
): Promise<unknown> {
  return requestInCompany<unknown>('/roles/revoke', companyId, {
    method: 'POST',
    body: assignBody(companyId, userId, roleCode),
  });
}

/**
 * Empty this company of every role one user holds in it, in one call.
 *
 * The company-scoped stand-in for `DELETE /roles/users/{id}`, which erases a user's
 * roles in every company at once and is refused to anyone but a platform
 * administrator. Answers `{revoked_count}` — worth showing, because "0" is the honest
 * answer when the person held nothing here and is otherwise indistinguishable from
 * success.
 *
 * `departmentId` narrows the wipe to one department instead of the whole company.
 */
export function revokeRolesInCompany(
  companyId: string,
  userId: string,
  departmentId?: string,
): Promise<{revoked_count?: number}> {
  return requestInCompany<{revoked_count?: number}>(
    '/roles/revoke-scope',
    companyId,
    {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        // Required here — unlike assign/revoke there is no unscoped case.
        company_id: companyId,
        ...(departmentId ? {department_id: departmentId} : {}),
      }),
    },
  );
}

/**
 * The same role to (or from) many accounts at once — up to 500 per call, chunked here
 * so a caller never has to know the cap.
 *
 * ⚠️ These two answer **500 for ordinary input errors**. The gateway swallows exactly
 * one gRPC code per direction as success (AlreadyExists on assign, NotFound on revoke)
 * and wraps everything else — an unknown `role_code`, a role not assignable in this
 * domain — into an internal error indistinguishable from sv_permissions being down. Do
 * not retry blind on a 500 from here; read the message.
 */
function bulkRoleBody(
  companyId: string,
  roleCode: string,
): (userIds: string[]) => string {
  return userIds =>
    JSON.stringify({
      user_ids: userIds,
      role_code: roleCode,
      domain_type: DOMAIN_TYPE_COMPANY,
      company_id: companyId,
    });
}

export function bulkAssignRole(
  companyId: string,
  userIds: string[],
  roleCode: string,
): Promise<BulkOutcome> {
  const body = bulkRoleBody(companyId, roleCode);
  return runBulk(userIds, batch =>
    requestInCompany<BulkOutcome>('/roles/bulk/assign', companyId, {
      method: 'POST',
      body: body(batch),
    }),
  );
}

export function bulkRevokeRole(
  companyId: string,
  userIds: string[],
  roleCode: string,
): Promise<BulkOutcome> {
  const body = bulkRoleBody(companyId, roleCode);
  return runBulk(userIds, batch =>
    requestInCompany<BulkOutcome>('/roles/bulk/revoke', companyId, {
      method: 'POST',
      body: body(batch),
    }),
  );
}
